import React, { useState, useEffect } from 'react';
import styles from './Decisions.module.scss';
import AddIcon from '@spectrum-icons/workflow/Add';
import Edit from '@spectrum-icons/workflow/Edit';
import { DecisionScreen } from '../../DecisionScreen/DecisionScreen';
import { EditOnlyWrapper } from '../context/RBACContext';
import { JIRA_CORP_LINK } from '../../../../utils/constants';

// Normalise any stored date format to "12 Jan 2026" for display.
// Handles: YYYY-MM-DD, DD/MM/YYYY (legacy Jira), "Sep 18, 2025" (legacy calendar),
// and pass-through for Q1 2025 / Sep 2025 (quarter/month selections).
const formatDecisionDate = (date: string): string => {
  if (!date) return '';

  // Quarter / month — pass through unchanged
  if (/^Q\d/.test(date) || /^[A-Za-z]{3} \d{4}$/.test(date)) return date;

  let iso = date;

  // DD/MM/YYYY → YYYY-MM-DD
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
    const [d, m, y] = date.split('/').map(Number);
    iso = `${y}-${m.toString().padStart(2, '0')}-${d
      .toString()
      .padStart(2, '0')}`;
  }
  // "Sep 18, 2025" → YYYY-MM-DD (manual parse to avoid new Date() local-timezone off-by-one)
  else if (/^[A-Za-z]{3} \d{1,2}, \d{4}$/.test(date)) {
    const MONTHS: Record<string, number> = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12
    };
    const [mon, dayComma, yearStr] = date.split(' ');
    const m = MONTHS[mon];
    const d = parseInt(dayComma, 10);
    const y = parseInt(yearStr, 10);
    if (m && !isNaN(d) && !isNaN(y)) {
      iso = `${y}-${m.toString().padStart(2, '0')}-${d
        .toString()
        .padStart(2, '0')}`;
    }
  }

  // Parse YYYY-MM-DD and format as "12 Jan 2026"
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number);
    const utc = new Date(Date.UTC(y, m - 1, d));
    return utc.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC'
    });
  }

  return date; // Unknown format — display as-is
};

// Helper function to format ADR links
const formatLinkDisplay = (
  link: string
): { displayText: string; isADR: boolean } => {
  // Check if the link contains ADR pattern
  // Common patterns: ADR-123, adr-123, /ADR-123, /adr/123, etc.
  const adrPatterns = [
    /ADR[_-]?(\d+)/i, // Matches ADR-123, ADR_123, ADR123
    /\/adr\/(\d+)/i, // Matches /adr/123
    /adr[_-]?(\d+)/i // Matches adr-123, adr_123
  ];

  for (const pattern of adrPatterns) {
    const match = link.match(pattern);
    if (match && match[1]) {
      return {
        displayText: `ADR-${match[1]}`,
        isADR: true
      };
    }
  }

  // Not an ADR link, return "Link" as display text to prevent long URLs from breaking layout
  return {
    displayText: 'Link',
    isADR: false
  };
};

const getDecisionLinkHref = (link: string): string => {
  const trimmedLink = (link || '').trim();
  if (!trimmedLink) return '';

  // Keep full URLs as-is.
  if (/^https?:\/\//i.test(trimmedLink)) {
    return trimmedLink;
  }

  // Convert ADR shorthand (e.g. ADR-123) to an absolute Jira browse URL.
  if (/^ADR-\d+$/i.test(trimmedLink)) {
    const normalizedAdrKey = trimmedLink.toUpperCase();
    return `https://${JIRA_CORP_LINK[0]}/browse/${normalizedAdrKey}`;
  }

  return trimmedLink;
};

interface Decision {
  date: string;
  id: string;
  description: string;
  link?: string;
}

interface DecisionsProps {
  decisions: Decision[];
  issueId?: string; // Add issueId for API calls
  addDecisionRef?: React.MutableRefObject<(() => void) | null>; // Ref to expose openAddPopup function
  isExpanded?: boolean; // Control visibility of decisions list
  onError?: (error: string) => void; // Add error callback for parent to show error banners
  onDataRefresh?: (patchResponse?: unknown) => void; // Add callback for seamless data refresh
  momentName?: string; // For audit logging
  momentType?: string; // For audit logging - RSD Type
}

export function Decisions({
  decisions,
  issueId,
  addDecisionRef,
  isExpanded = true,
  onError,
  onDataRefresh,
  momentName,
  momentType
}: DecisionsProps) {
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [decisionsData, setDecisionsData] = useState<Decision[]>(decisions);
  const [selectedDecision, setSelectedDecision] = useState<Decision | null>(
    null
  );

  // State for tracking expanded descriptions and content heights
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set()
  );
  const [descriptionHeights, setDescriptionHeights] = useState<
    Record<string, number>
  >({});
  const descriptionRefs = React.useRef<Record<string, HTMLDivElement | null>>(
    {}
  );

  const MAX_COLLAPSED_HEIGHT = 150; // Max height before showing "Read More"

  // Update local state when decisions prop changes
  useEffect(() => {
    setDecisionsData(decisions);
  }, [decisions]);

  // Measure description heights after render
  useEffect(() => {
    const newHeights: Record<string, number> = {};
    decisionsData.forEach(decision => {
      const ref = descriptionRefs.current[decision.id];
      if (ref) {
        newHeights[decision.id] = ref.scrollHeight;
      }
    });
    setDescriptionHeights(newHeights);
  }, [decisionsData]);

  const toggleExpanded = (decisionId: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(decisionId)) {
        newSet.delete(decisionId);
      } else {
        newSet.add(decisionId);
      }
      return newSet;
    });
  };

  const openAddPopup = () => {
    setIsAddPopupOpen(true);
  };

  // Expose openAddPopup function to parent via ref
  React.useEffect(() => {
    if (addDecisionRef) {
      addDecisionRef.current = openAddPopup;
    }
  }, [addDecisionRef, openAddPopup]);

  const closeAddPopup = () => {
    setIsAddPopupOpen(false);
  };

  const openEditPopup = (decision: Decision) => {
    setSelectedDecision(decision);
    setIsEditPopupOpen(true);
  };

  const closeEditPopup = () => {
    setIsEditPopupOpen(false);
    setSelectedDecision(null);
  };

  const handleSaveDecision = (decisionData: any) => {
    // Since DecisionScreen now handles the API call,
    // we only need to update local state as fallback or for immediate UI feedback
    const newDecision: Decision = {
      date: decisionData.date,
      id: String(Date.now()), // Temporary ID for local state only - backend will provide real ID
      description: decisionData.decision,
      link: decisionData.link
    };

    // Update the local state for immediate UI feedback
    setDecisionsData(prev => [...prev, newDecision]);
  };

  const handleUpdateDecision = (decisionData: any) => {
    // Update the decision in the array for optimistic update
    setDecisionsData(prev =>
      prev.map(item =>
        item.id === decisionData.id || item.id === selectedDecision?.id
          ? {
              ...item,
              date: decisionData.date,
              description: decisionData.decision,
              link: decisionData.link
            }
          : item
      )
    );
  };

  const handleDeleteDecision = (decisionId: string) => {
    // Remove the decision from the array
    setDecisionsData(prev => prev.filter(item => item.id !== decisionId));
  };

  return (
    <div
      className={styles.decisionsContainer}
      data-testid="decisions-container"
    >
      {/* Decision Screen for Adding */}
      <DecisionScreen
        isOpen={isAddPopupOpen}
        onClose={closeAddPopup}
        onSave={handleSaveDecision}
        onError={onError}
        onDataRefresh={onDataRefresh} // Pass seamless refresh callback
        issueId={issueId}
        momentName={momentName} // For audit logging
        momentType={momentType} // ✅ ADD: For audit logging - RSD Type
        existingDecisions={decisionsData.map(d => ({
          date: d.date,
          id: d.id,
          description: d.description,
          link: d.link
        }))}
        data-testid="decisions-add-screen"
      />

      {/* Decision Screen for Editing */}
      <DecisionScreen
        isOpen={isEditPopupOpen}
        onClose={closeEditPopup}
        onSave={handleUpdateDecision}
        onDelete={handleDeleteDecision}
        onError={onError}
        onDataRefresh={onDataRefresh} // Pass seamless refresh callback
        issueId={issueId}
        momentName={momentName} // For audit logging
        momentType={momentType} // ✅ ADD: For audit logging - RSD Type
        existingDecisions={decisionsData.map(d => ({
          date: d.date,
          id: d.id,
          description: d.description,
          link: d.link
        }))}
        isEditMode={true}
        decisionToEdit={
          selectedDecision
            ? {
                id: selectedDecision.id,
                date: selectedDecision.date,
                description: selectedDecision.description,
                link: selectedDecision.link
              }
            : undefined
        }
        data-testid="decisions-edit-screen"
      />

      {/* Only show decisions list when expanded */}
      {isExpanded && (
        <div className={styles.decisionsList} data-testid="decisions-list">
          {decisionsData.length === 0 ? (
            <div
              className={styles.emptyState}
              data-testid="decisions-empty-state"
            >
              <p className={styles.emptyMessage}>
                No decisions have been added yet.
              </p>
              <EditOnlyWrapper>
                <button
                  className={styles.addFirstDecisionButton}
                  onClick={openAddPopup}
                  data-testid="decisions-add-first-button"
                >
                  Add Decision
                </button>
              </EditOnlyWrapper>
            </div>
          ) : (
            decisionsData.map((decision, index) => (
              <div
                key={index}
                className={styles.decisionItem}
                data-testid={`decisions-item-${index}`}
              >
                <div
                  className={styles.decisionContent}
                  data-testid={`decisions-content-${index}`}
                >
                  <div
                    className={styles.date}
                    data-testid={`decisions-date-${index}`}
                  >
                    {formatDecisionDate(decision.date)}
                  </div>
                  <div
                    className={styles.link}
                    data-testid={`decisions-advlink-${index}`}
                  >
                    {decision.link
                      ? (() => {
                          const { displayText } = formatLinkDisplay(
                            decision.link
                          );
                          const linkHref = getDecisionLinkHref(decision.link);
                          return (
                            <a
                              href={linkHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.advLinkUrl}
                              title={decision.link}
                            >
                              {displayText}
                            </a>
                          );
                        })()
                      : '-'}
                  </div>
                  <div className={styles.descriptionWrapper}>
                    <div
                      ref={el => {
                        descriptionRefs.current[decision.id] = el;
                      }}
                      className={`${styles.description} ${
                        !expandedDescriptions.has(decision.id) &&
                        descriptionHeights[decision.id] > MAX_COLLAPSED_HEIGHT
                          ? styles.collapsed
                          : ''
                      }`}
                      style={{
                        maxHeight:
                          !expandedDescriptions.has(decision.id) &&
                          descriptionHeights[decision.id] > MAX_COLLAPSED_HEIGHT
                            ? `${MAX_COLLAPSED_HEIGHT}px`
                            : 'none'
                      }}
                      data-testid={`decisions-description-${index}`}
                    >
                      {decision.description}
                    </div>
                    {descriptionHeights[decision.id] > MAX_COLLAPSED_HEIGHT && (
                      <button
                        className={styles.readMoreButton}
                        onClick={e => {
                          e.stopPropagation();
                          toggleExpanded(decision.id);
                        }}
                        aria-label={
                          expandedDescriptions.has(decision.id)
                            ? 'Read Less'
                            : 'Read More'
                        }
                      >
                        {expandedDescriptions.has(decision.id)
                          ? 'Read Less'
                          : 'Read More'}
                      </button>
                    )}
                  </div>
                  <EditOnlyWrapper>
                    <button
                      className={styles.editButton}
                      onClick={() => openEditPopup(decision)}
                      data-testid={`decisions-edit-button-${index}`}
                    >
                      <div
                        className={styles.iconWrapper}
                        data-testid={`decisions-edit-icon-wrapper-${index}`}
                      >
                        <Edit
                          size="S"
                          data-testid={`decisions-edit-icon-${index}`}
                        />
                      </div>
                    </button>
                  </EditOnlyWrapper>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
